import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent
} from 'react';
import { createPortal } from 'react-dom';
import { Button } from './primitives/Button';
import { IconButton } from './primitives/IconButton';

export type AttachWorkItemType = 'epic' | 'story' | 'bug' | 'task' | 'subtask';

export interface AttachWorkItemRow {
  id: string;
  title: string;
  type: AttachWorkItemType;
  status: string;
  assigneeName: string | null;
  updatedAt: string;
  /** Optional preview body shown in the side-rail when a row title is clicked. */
  bodyExcerpt?: string;
}

/**
 * Mode controls the cover's title, aria-label, counter copy, and how the
 * `subjectId` is exposed in the rendered `data-*` attribute (so different
 * test fixtures can target the right surface).
 *
 *   - 'attach-to-document' (default): legacy mode — used by AttachedWorkItemsPanel
 *     on the document editor to attach work items to a document.
 *   - 'attach-as-child': used by ChildTasksPanel / SubtasksChecklist on the
 *     work-item detail page to link existing work items as children of a
 *     parent work item (sets `parent_work_item_id = subjectId`).
 */
export type AttachWorkItemCoverMode = 'attach-to-document' | 'attach-as-child';

export interface AttachWorkItemCoverProps {
  open: boolean;
  onClose: () => void;
  onAttach: (workItemIds: string[]) => Promise<void>;
  /**
   * The subject this cover is attaching items *to*. For
   * `attach-to-document` mode this is the document id; for
   * `attach-as-child` mode this is the parent work item id.
   * Backwards-compatible alias `documentId` is still accepted.
   */
  subjectId?: string;
  /** Backwards-compatible alias for `subjectId` (legacy callers). */
  documentId?: string;
  /** Optional mode — defaults to 'attach-to-document'. */
  mode?: AttachWorkItemCoverMode;
  /**
   * Caller-supplied work item list. The primitive is presentational —
   * the route layer owns the React Query / Supabase fetch.
   */
  workItems: AttachWorkItemRow[];
}

const COVER_LABEL: Record<AttachWorkItemCoverMode, string> = {
  'attach-to-document': 'Attach to Work Item',
  'attach-as-child': 'Attach child work item'
};

type SortKey = 'title' | 'type' | 'status' | 'assigneeName' | 'updatedAt';
type TypeFilter = 'all' | AttachWorkItemType;

const TYPE_LABEL: Record<AttachWorkItemType, string> = {
  epic: 'Epic',
  story: 'Story',
  bug: 'Bug',
  task: 'Task',
  subtask: 'Subtask'
};

const TYPE_FILTERS: ReadonlyArray<TypeFilter> = [
  'all',
  'epic',
  'story',
  'bug',
  'task',
  'subtask'
];

function compare(a: string | null, b: string | null): number {
  if (a == null && b == null) return 0;
  if (a == null) return -1;
  if (b == null) return 1;
  return a.localeCompare(b);
}

function formatTimestamp(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
}

export function AttachWorkItemCover({
  open,
  onClose,
  onAttach,
  subjectId,
  documentId,
  mode = 'attach-to-document',
  workItems
}: AttachWorkItemCoverProps) {
  const subject = subjectId ?? documentId ?? '';
  const label = COVER_LABEL[mode];
  const [query, setQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');
  const [sortKey, setSortKey] = useState<SortKey>('title');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [previewId, setPreviewId] = useState<string | null>(null);
  const [attaching, setAttaching] = useState(false);
  const panelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setTypeFilter('all');
    setSelectedIds([]);
    setPreviewId(null);
    panelRef.current?.focus();
  }, [open]);

  const visibleRows = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const filtered = workItems.filter((w) => {
      if (typeFilter !== 'all' && w.type !== typeFilter) return false;
      if (!needle) return true;
      return w.title.toLowerCase().includes(needle);
    });
    const sorted = [...filtered].sort((a, b) => {
      const av =
        sortKey === 'title'
          ? a.title
          : sortKey === 'type'
            ? TYPE_LABEL[a.type]
            : sortKey === 'status'
              ? a.status
              : sortKey === 'assigneeName'
                ? a.assigneeName
                : a.updatedAt;
      const bv =
        sortKey === 'title'
          ? b.title
          : sortKey === 'type'
            ? TYPE_LABEL[b.type]
            : sortKey === 'status'
              ? b.status
              : sortKey === 'assigneeName'
                ? b.assigneeName
                : b.updatedAt;
      const cmp = compare(av, bv);
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return sorted;
  }, [workItems, query, typeFilter, sortKey, sortDir]);

  const previewItem = useMemo(
    () => (previewId ? workItems.find((w) => w.id === previewId) ?? null : null),
    [previewId, workItems]
  );

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const toggleSelection = useCallback((id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }, []);

  const handleDone = async () => {
    if (selectedIds.length === 0 || attaching) return;
    setAttaching(true);
    try {
      await onAttach([...selectedIds]);
      onClose();
    } finally {
      setAttaching(false);
    }
  };

  const handleKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      onClose();
    }
  };

  if (!open) return null;

  const counter = `${selectedIds.length} work item${selectedIds.length === 1 ? '' : 's'} selected`;

  return createPortal(
    <div
      className="pn-attach-cover-backdrop"
      data-testid="attach-work-item-cover-backdrop"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={panelRef}
        className="pn-attach-cover"
        role="dialog"
        aria-modal="true"
        aria-label={label}
        data-testid="attach-work-item-cover"
        data-mode={mode}
        data-subject-id={subject}
        data-document-id={mode === 'attach-to-document' ? subject : undefined}
        data-parent-work-item-id={mode === 'attach-as-child' ? subject : undefined}
        tabIndex={-1}
        onKeyDown={handleKey}
      >
        <header className="pn-attach-cover-head">
          <div className="pn-attach-cover-titlebar">
            <h2 className="pn-attach-cover-title">{label}</h2>
            <span
              className="pn-attach-cover-counter"
              data-testid="attach-work-item-cover-counter"
            >
              {counter}
            </span>
          </div>
          <div className="pn-attach-cover-actions">
            <Button
              type="button"
              variant="ghost"
              onClick={onClose}
              data-testid="attach-work-item-cover-cancel"
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="primary"
              onClick={handleDone}
              disabled={selectedIds.length === 0 || attaching}
              data-testid="attach-work-item-cover-done"
            >
              {attaching ? 'Attaching…' : 'Done'}
            </Button>
            <IconButton
              aria-label="Close attach work item cover"
              data-testid="attach-work-item-cover-close"
              onClick={onClose}
            >
              ×
            </IconButton>
          </div>
        </header>

        <div className="pn-attach-cover-toolbar">
          <input
            type="search"
            className="pn-attach-cover-search"
            placeholder="Search work items…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            data-testid="attach-work-item-cover-search"
            aria-label="Search work items"
          />
          <div
            className="pn-attach-cover-chips"
            role="radiogroup"
            aria-label="Filter by type"
          >
            {TYPE_FILTERS.map((v) => (
              <button
                key={v}
                type="button"
                className="pn-attach-cover-chip"
                role="radio"
                aria-checked={typeFilter === v}
                data-testid={`attach-work-item-cover-chip-${v}`}
                data-active={typeFilter === v ? 'true' : 'false'}
                onClick={() => setTypeFilter(v)}
              >
                {v === 'all' ? 'All' : TYPE_LABEL[v]}
              </button>
            ))}
          </div>
        </div>

        <div className="pn-attach-cover-body">
          <div
            className="pn-attach-cover-list"
            data-testid="attach-work-item-cover-list"
            data-cols="6"
            data-row-count={visibleRows.length}
          >
            <div
              className="pn-attach-cover-list-head"
              role="row"
              aria-label="Work item columns"
            >
              <span className="pn-attach-cover-checkbox-col" role="columnheader">
                <span className="pn-visually-hidden">Select</span>
              </span>
              {(
                [
                  ['title', 'Title'],
                  ['type', 'Type'],
                  ['status', 'Status'],
                  ['assigneeName', 'Assignee'],
                  ['updatedAt', 'Updated']
                ] as Array<[SortKey, string]>
              ).map(([key, label]) => {
                const sorted = sortKey === key;
                return (
                  <span
                    key={key}
                    className="pn-attach-cover-list-head-cell"
                    role="columnheader"
                    data-testid={`attach-work-item-cover-header-${key}`}
                    data-sort={sorted ? sortDir : 'none'}
                  >
                    <button
                      type="button"
                      className="pn-attach-cover-header-btn"
                      onClick={() => toggleSort(key)}
                    >
                      <span>{label}</span>
                      <span aria-hidden="true">
                        {sorted ? (sortDir === 'asc' ? '↑' : '↓') : ''}
                      </span>
                    </button>
                  </span>
                );
              })}
            </div>
            <ul className="pn-attach-cover-rows" role="list">
              {visibleRows.length === 0 ? (
                <li
                  className="pn-attach-cover-empty"
                  data-testid="attach-work-item-cover-empty"
                  role="listitem"
                >
                  No work items match.
                </li>
              ) : (
                visibleRows.map((w) => {
                  const checked = selectedIds.includes(w.id);
                  return (
                    <li
                      key={w.id}
                      className="pn-attach-cover-row"
                      role="listitem"
                      data-testid={`attach-work-item-cover-row-${w.id}`}
                      data-selected={checked ? 'true' : 'false'}
                      data-previewing={previewId === w.id ? 'true' : 'false'}
                    >
                      <span className="pn-attach-cover-checkbox-col">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleSelection(w.id)}
                          data-testid={`attach-work-item-cover-checkbox-${w.id}`}
                          aria-label={`Select ${w.title}`}
                        />
                      </span>
                      <span className="pn-attach-cover-row-title">
                        <button
                          type="button"
                          className="pn-attach-cover-title-btn"
                          onClick={() => setPreviewId(w.id)}
                          data-testid={`attach-work-item-cover-title-${w.id}`}
                        >
                          {w.title}
                        </button>
                      </span>
                      <span data-type={w.type}>{TYPE_LABEL[w.type]}</span>
                      <span>{w.status}</span>
                      <span>{w.assigneeName ?? '—'}</span>
                      <span>{formatTimestamp(w.updatedAt)}</span>
                    </li>
                  );
                })
              )}
            </ul>
          </div>

          {previewItem ? (
            <aside
              className="pn-attach-cover-preview"
              aria-label="Work item preview"
              data-testid="attach-work-item-cover-preview"
            >
              <header>
                <h3 data-testid="attach-work-item-cover-preview-title">
                  {previewItem.title}
                </h3>
                <IconButton
                  aria-label="Close preview"
                  data-testid="attach-work-item-cover-preview-close"
                  onClick={() => setPreviewId(null)}
                >
                  ×
                </IconButton>
              </header>
              <p className="pn-attach-cover-preview-meta">
                {TYPE_LABEL[previewItem.type]} · {previewItem.status} ·{' '}
                {previewItem.assigneeName ?? 'Unassigned'} ·{' '}
                {formatTimestamp(previewItem.updatedAt)}
              </p>
              <div className="pn-attach-cover-preview-body">
                {previewItem.bodyExcerpt ?? 'No preview available.'}
              </div>
            </aside>
          ) : null}
        </div>
      </div>
    </div>,
    document.body
  );
}
